import React, { useState, useMemo, useContext } from 'react';
import cx from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { map, reject, get } from 'lodash-es';

import AddToList from '../../../../components/AddToList';
import { MARKET_LIST_TYPE } from '../../../../components/AddToList/listTypeConfig';
import { SortableTable } from '../../../../components/SortableTable';
import SimpleCheckbox from '../../../../components/Checkbox/SimpleCheckbox';
import Button from '../../../../components/Buttons/Button';
import Tooltip from '../../../../components/Tooltip';
import { skeletonTable } from '../../../../components/Skeleton';
import QuickViewContext from '../../../../components/QuickView/QuickViewContext';

import {
  mainColumns,
  checkboxColumn,
  columnsDataFields,
  buildDataRows,
  buildFreeDataRows,
} from './config';

import { isUnified } from '../../../../utils/helpers';

import { fetchApps } from '../../actions/apps-actions';
import {
  appsSelector,
  appsTotalsSelector,
  appsTotalCountSelector,
  appsSettingsSelector,
  appsFiltersSelector,
} from '../../selectors';
import { initParams } from '../../configs';
import { DEFAULT_APPS_FILTERS } from '../../configs/constants';

const { permissions, currentUser, store } = initParams;
const { maxAllowedApps, isFlexUser } = permissions;
const allowMyMarkets = permissions.allowMyMarkets && !isFlexUser;
const FREE_ROWS_LIMIT = 10;

const AppsTable = () => {
  const dispatch = useDispatch();
  const apps = useSelector(appsSelector);
  const totals = useSelector(appsTotalsSelector);
  const totalCount = useSelector(appsTotalCountSelector);
  const settings = useSelector(appsSettingsSelector);
  const filters = useSelector(appsFiltersSelector);

  const { onAppQuickViewOpen, onPubQuickViewOpen } = useContext(QuickViewContext);

  const [selectedApps, setSelectedApps] = useState([]);

  const isFree = !permissions.allowPaidData;
  const visibleApps = isFree ? apps.slice(0, FREE_ROWS_LIMIT) : apps;

  const isInitialLoading = !settings.loaded && settings.pending;
  const isLoadingMore = settings.loaded && settings.pending && settings.mode === 'append';
  const isReplacing = settings.loaded && settings.pending && settings.mode !== 'append';
  const hasMore = !isFree && apps.length < totalCount;

  const selectableApps = apps.filter((app) => get(app, 'available', true));
  const allAppsSelected =
    selectableApps.length > 0 &&
    (selectedApps.length === selectableApps.length || selectedApps.length >= maxAllowedApps);
  const limitReached = () => selectedApps.length >= maxAllowedApps;

  const handleSort = ({ sortBy, sortOrder }) => {
    dispatch(fetchApps({ metric: sortBy, order: sortOrder, mode: 'replace' }));
    setSelectedApps([]);
  };

  const handleShowMore = () => dispatch(fetchApps({ mode: 'append' }));

  const handleAllAppsChange = (checked) => {
    const appsToSelect = selectableApps.slice(0, maxAllowedApps);
    setSelectedApps(checked ? map(appsToSelect, (app) => app) : []);
  };

  const handleAppsChange = (checked, app) => {
    setSelectedApps((prev) => {
      if (checked) return [...prev, app];
      return reject(prev, ({ ref_no }) => ref_no === app.ref_no);
    });
  };

  const handleMarketSubmit = () => setSelectedApps([]);

  const columns = useMemo(() => {
    const sortedColumns = mainColumns.map((col) => ({
      ...col,
      sortable: col.sortable && !isFlexUser,
      sortOrder: col.sortBy === filters.metric ? filters.order : undefined,
    }));

    if (!allowMyMarkets) return sortedColumns;

    return [
      {
        ...checkboxColumn,
        element: (
          <SimpleCheckbox
            id="check-all-apps"
            checked={allAppsSelected}
            onChange={(checked) => handleAllAppsChange(checked)}
          />
        ),
      },
      ...sortedColumns,
    ];
  }, [filters.metric, filters.order, allAppsSelected, selectableApps.length]);

  const dataRows = useMemo(
    () =>
      isFree
        ? buildFreeDataRows({
            apps: visibleApps,
            isProspect: permissions.prospect,
            isFlexUser: permissions.isFlexUser,
          })
        : buildDataRows({
            apps,
            totals,
            selectedApps,
            limitReached,
            isFlexUser: permissions.isFlexUser,
            onAppQuickViewOpen,
            onPubQuickViewOpen,
            onAppCheckboxChange: handleAppsChange,
          }),
    [apps, totals, selectedApps, onAppQuickViewOpen, onPubQuickViewOpen, isFree, visibleApps]
  );

  const initialSkeletonRows = useMemo(
    () => skeletonTable({ columns: columnsDataFields, rowsNumber: 10, height: 12 }),
    []
  );
  const moreSkeletonRows = useMemo(
    () => skeletonTable({ columns: columnsDataFields, rowsNumber: 5, height: 12 }),
    []
  );

  const showMoreRow = useMemo(
    () => ({
      rowClassName: 'trigger-show-more',
      app_name: (
        <td className="text-xxs-left trigger-show-more" colSpan={allowMyMarkets ? 8 : 7}>
          <Button
            type="button"
            className="p-a-xs d-block btn-link publisher_page_apps__show_more_btn"
            onClick={handleShowMore}
          >
            <i className="hicon-plus-circle" />
            Show ({Math.min(DEFAULT_APPS_FILTERS.limit, totalCount - apps.length)}) more apps…
          </Button>
        </td>
      ),
    }),
    [allowMyMarkets, totalCount, apps.length]
  );

  const displayRows = useMemo(() => {
    if (isInitialLoading || isReplacing) return initialSkeletonRows;
    if (isLoadingMore) return [...dataRows, ...moreSkeletonRows];
    return hasMore ? [...dataRows, showMoreRow] : dataRows;
  }, [
    isInitialLoading,
    isReplacing,
    isLoadingMore,
    dataRows,
    initialSkeletonRows,
    moreSkeletonRows,
    showMoreRow,
    hasMore,
  ]);

  const addToMarketApps = selectedApps.map((app) => ({ ...app, kind: 'app', store: app.store }));

  return (
    <section className={cx('publisher_page_apps pt-lg mb-xxl', isUnified(store) ? 'b-t' : '')}>
      {isUnified(store) && (
        <div className="d-flex flex-items-xxs-middle mb-xs">
          <h2 className="text-md">
            <span className="mr-xs">Publisher’s Top Apps</span>
            <Tooltip
              content="Top apps are ranked by revenue. For instances where the publisher has apps for both stores the app icon, price and release date listed is for iOS while the revenue, downloads, MAU and DAU data is aggregated for both apps."
              placement="top"
            >
              <i className="hicon-question-circle text-xs text-quiet" />
            </Tooltip>
          </h2>
        </div>
      )}
      <div className="add-to-market-container pos-rel d-flex flex-items-xxs-middle mb-md">
        {allowMyMarkets && (
          <AddToList
            entities={addToMarketApps}
            buttonText="Add Selected To Market"
            listType={MARKET_LIST_TYPE}
            customerId={currentUser.customerId}
            currentUserId={currentUser.id}
            onSubmitCallback={handleMarketSubmit}
          />
        )}
      </div>
      <div className="pos-rel">
        <SortableTable
          tableClassName="table-default table-lg sticky-table-header"
          columns={columns}
          hasShowMoreCell={false}
          initialRowsLimit={displayRows.length}
          rows={displayRows}
          showEmptyCells={(!isFree && !isFlexUser) || isInitialLoading || isReplacing}
          sortParams={{ by: filters.metric, order: filters.order }}
          onSortEnd={handleSort}
        />
      </div>
    </section>
  );
};

export default AppsTable;
